/**
 * v3.15-1057 — Shared user grouping utility for assignee dropdowns.
 *
 * Groups a flat list of user objects by company_name for display in
 * <SelectGroup>/<SelectLabel> containers. Used by Deal Ownership
 * dropdowns, Task Assignment, and Money Manager client lookup.
 *
 * Strict scope: dropdown organization only. No filtering, eligibility,
 * permission, payroll, commission, or routing logic.
 */

/**
 * Resolve a user's company_name. Prefers the `company_name` field on the
 * user record; falls back to a lookup in the supplied companies array;
 * finally falls back to "External" for users with no company_id.
 */
const resolveCompanyName = (user, companiesById) => {
  if (user?.company_name) return user.company_name;
  if (companiesById && user?.company_id && companiesById[user.company_id]) {
    return companiesById[user.company_id];
  }
  return 'External';
};

/**
 * Group users by company_name, sorted alphabetically by company.
 * Within each group, users are sorted alphabetically by full_name.
 *
 * @param {Array<{id, full_name, company_id?, company_name?}>} users
 * @param {Object} [options]
 * @param {Array<{id, name}>} [options.companies] — used to resolve
 *        company_name when a user record lacks one
 * @param {string} [options.externalLabel='External'] — label for the
 *        bucket containing users with no resolvable company
 * @returns {Array<{ company_name: string, users: Array }>}
 */
export const groupUsersByCompany = (users, options = {}) => {
  const { companies = [], externalLabel = 'External' } = options;
  const companiesById = {};
  for (const c of companies) {
    if (c && c.id) companiesById[c.id] = c.name || externalLabel;
  }
  const groups = new Map();
  for (const u of users || []) {
    if (!u) continue;
    const name = u.company_name
      || (companiesById[u.company_id] || (u.company_id ? null : null))
      || resolveCompanyName(u, companiesById)
      || externalLabel;
    if (!groups.has(name)) groups.set(name, []);
    groups.get(name).push(u);
  }
  // Sort groups alphabetically, External always last.
  const sortedNames = Array.from(groups.keys()).sort((a, b) => {
    if (a === externalLabel) return 1;
    if (b === externalLabel) return -1;
    return a.localeCompare(b);
  });
  return sortedNames.map((name) => ({
    company_name: name,
    users: [...groups.get(name)].sort((a, b) =>
      String(a.full_name || '').localeCompare(String(b.full_name || ''))
    ),
  }));
};
